// Phase 4c — Integrations card.
//
// Fetches connection status for Instagram + Meta Ads + Google Ads in
// parallel. Renders a 4-tile grid (the 4th slot is a TikTok Shop stub
// since we don't have a backend integration for it yet — Google
// Merchant Center is the same; both render as "Not connected" with a
// "Coming soon" disabled CTA).
//
// Connect → POST /connect → redirect to the OAuth authorize URL.
// After auth, Meta bounces to the legacy /brand.html callback handler
// (it drives the picker modal). Once the user finishes setup there,
// they navigate back to /brand and see the connected state. Long-term
// fix: rebuild the picker modal in the new app + update the callback
// to honor a `redirect` state param like /auth/google does.
//
// Disconnect → DELETE /api/integrations/{type}/:credentialId.
//
// Bottom strip shows the brand's syncSettings (auto-sync toggle +
// cadences) with a Sync Now button that triggers an immediate
// catalog + posts sync via the existing endpoints.
//
// Instagram tile only: a Re-scan button sits alongside Manage/Disconnect
// for when the operator wants already-ingested posts re-analysed (e.g.
// after a detect-model change). It's the BILLABLE counterpart to Sync Now
// — force:true on sync-posts re-queues a detect run per re-entered post,
// each one a real cost — so it's gated behind an explicit window.confirm
// (same convention as disconnect below) instead of firing on click.

import { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Card, CardBody, HStack, VStack, Text, Heading, Box, Button, Badge,
  SimpleGrid, Flex, Divider, useToast, Icon, Spinner, Checkbox
} from '@chakra-ui/react';
import type { ChangeEvent } from 'react';
import { apiJson, isApiError } from '../../auth/apiFetch';
import type { Brand } from './types';
import { IGPickerModal } from './IGPickerModal';
import { AdsPickerModal } from './AdsPickerModal';
import { GoogleAdsPickerModal } from './GoogleAdsPickerModal';
import { ShopifyConnectModal } from './ShopifyConnectModal';
import { connectionStatusUrl, fetchConnectionStatus, type DirectConnection } from './connectionStatus';

// Imperative handle exposed by IntegrationsCard so parents (like the
// /brand page) can pop the picker open after the post-OAuth bounce
// — see the useSearchParamsConsumer wiring in Brand/index.tsx.
export type IntegrationsCardHandle = {
  openIGPicker:        (credentialId: string) => void;
  openAdsPicker:       (credentialId: string) => void;
  openGoogleAdsPicker: (credentialId: string) => void;
};

type IntegrationKind = 'instagram' | 'meta-ads' | 'google-ads' | 'shopify' | 'tiktok-shop';

type StatusRow = {
  configured: boolean;
  credential: null | {
    id:           string;
    status:       'active' | 'pending';
    igUsername?:  string | null;
    pageName?:    string | null;
    selectedAdAccountName?: string | null;
    accountLabel?: string | null;
    lastUsedAt?:  string | null;
    connectedAt?: string | null;
  };
};

// Shopify's /status response shape is distinct — carries shopInfo +
// productCount + lastWebhookAt + webhookSubs that no other integration
// surfaces. Backend: routes/integrations.js GET /shopify/status.
type ShopifyStatusRow = {
  configured: boolean;
  // 2026-10-16 — 'custom' | 'public_staging' | 'public'. Determines
  // which install UX the tile + modal show. Custom = legacy
  // request-access-then-install two-path flow. Public = direct Install
  // (no operator approval, no allowlist, access-request UX suppressed).
  appMode?: 'custom' | 'public_staging' | 'public';
  credential: null | {
    _id:         string;
    shopDomain:  string | null;
    shopInfo:    null | {
      shopName?:     string | null;
      currencyCode?: string | null;
      planName?:     string | null;
      isDevStore?:   boolean | null;
    };
    scopes:            string[];
    connectedAt:       string | null;
    lastUsedAt:        string | null;
    lastCatalogSyncAt: string | null;
    lastWebhookAt:     string | null;
    productCount:      number | null;
    webhookSubs:       Array<{ topic: string; subscriptionId: string }>;
  };
  syncSettings?: {
    shopifyAutoPushImages: boolean;
  };
  // 2026-10-15 — surfaces pending/ready/rejected access-request state
  // on the tile so brand can close the modal and come back later. Null
  // when there's no non-terminal request OR when the terminal is >24h
  // old on a connected brand (don't nag with 'installed 3 months ago').
  accessRequest?: null | {
    _id:         string;
    status:      'pending' | 'ready' | 'installed' | 'rejected';
    shopDomain:  string;
    createdAt:   string;
    approvedAt:  string | null;
    rejectedAt:  string | null;
    installedAt: string | null;
    notes:       string | null;
  };
};

type Props = {
  brand: Brand;
  // When set, render only this provider's tile and its controls.
  // /integrations and /brand omit it and keep the full grid.
  only?: DirectConnection;
};

// Counters from POST sync-posts — real numbers for the Re-scan result toast.
// reIngested (already-known posts refreshed + re-queued for detect) is what
// actually moves on a force re-scan; ingested (genuinely new posts) rarely
// does, since Re-scan targets an account that's already been synced before.
type SyncPostsResponse = {
  ok:           boolean;
  fetched:      number;
  ingested:     number;
  reIngested:   number;
  skipped:      number;
  capSkipped:   number;
  errors:       number;
  queuedRunIds: string[];
};

export const IntegrationsCard = forwardRef<IntegrationsCardHandle, Props>(function IntegrationsCard({ brand, only }, ref) {
  const toast = useToast();
  const [igStatus,    setIgStatus]    = useState<StatusRow | null>(null);
  const [metaStatus,  setMetaStatus]  = useState<StatusRow | null>(null);
  const [googleStatus, setGoogleStatus] = useState<StatusRow | null>(null);
  const [shopifyStatus, setShopifyStatus] = useState<ShopifyStatusRow | null>(null);
  const [igError, setIgError] = useState<string | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [shopifyError, setShopifyError] = useState<string | null>(null);
  const brandIdRef = useRef(brand._id);
  brandIdRef.current = brand._id;
  const [shopifyModalOpen, setShopifyModalOpen] = useState(false);
  const [shopifySyncing, setShopifySyncing] = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [syncing,     setSyncing]     = useState(false);
  // Keyed by credentialId (rather than a plain boolean) so the Re-scan
  // button on THIS tile only shows its own busy state — same scoping the
  // billable call itself uses via ?credentialId=.
  const [rescanningId, setRescanningId] = useState<string | null>(null);
  const [igPickerCredId,        setIgPickerCredId]        = useState<string | null>(null);
  const [adsPickerCredId,       setAdsPickerCredId]       = useState<string | null>(null);
  const [googleAdsPickerCredId, setGoogleAdsPickerCredId] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    openIGPicker:        (credId) => setIgPickerCredId(credId),
    openAdsPicker:       (credId) => setAdsPickerCredId(credId),
    openGoogleAdsPicker: (credId) => setGoogleAdsPickerCredId(credId)
  }), []);

  const refresh = useCallback(async () => {
    const requestedId = brand._id;
    setLoading(true);
    try {
      const want = (kind: DirectConnection) => !only || only === kind;
      // Same four status routes as before. A filtered section skips the
      // providers it does not render. A rejected read is Error on the
      // tile — not Not connected, and not a Connect button.
      const [ig, meta, google, shopify] = await Promise.all([
        want('instagram') ? fetchConnectionStatus<StatusRow>('instagram', requestedId) : Promise.resolve(null),
        want('meta-ads') ? fetchConnectionStatus<StatusRow>('meta-ads', requestedId) : Promise.resolve(null),
        want('google-ads') ? fetchConnectionStatus<StatusRow>('google-ads', requestedId) : Promise.resolve(null),
        want('shopify') ? fetchConnectionStatus<ShopifyStatusRow>('shopify', requestedId) : Promise.resolve(null)
      ]);
      if (brandIdRef.current !== requestedId) return;
      setIgStatus(ig && ig.ok ? ig.body : null);
      setMetaStatus(meta && meta.ok ? meta.body : null);
      setGoogleStatus(google && google.ok ? google.body : null);
      setShopifyStatus(shopify && shopify.ok ? shopify.body : null);
      setIgError(ig && !ig.ok ? ig.error : null);
      setMetaError(meta && !meta.ok ? meta.error : null);
      setGoogleError(google && !google.ok ? google.error : null);
      setShopifyError(shopify && !shopify.ok ? shopify.error : null);
    } finally {
      if (brandIdRef.current === requestedId) setLoading(false);
    }
  }, [brand._id, only]);

  useEffect(() => { void refresh(); }, [refresh]);

  // 2026-10-15 — poll /shopify/status every 30s when there's a pending
  // or ready access request so the tile flips from "Awaiting approval"
  // → "Ready to install" without the brand needing to refresh. Only
  // polls while the UX gap exists; stops once the brand connects or
  // closes the request.
  const shopifyRequestStatus = shopifyStatus?.accessRequest?.status || null;
  const shouldPollShopify = shopifyRequestStatus === 'pending' || shopifyRequestStatus === 'ready';
  useEffect(() => {
    if (!shouldPollShopify || !brand._id) return;
    const brandId = brand._id;
    const interval = window.setInterval(() => {
      apiJson<ShopifyStatusRow>(connectionStatusUrl('shopify', brandId))
        .then((r) => setShopifyStatus(r))
        .catch(() => { /* transient — keep polling */ });
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [shouldPollShopify, brand._id]);

  const connect = async (kind: IntegrationKind) => {
    if (kind !== 'instagram' && kind !== 'meta-ads' && kind !== 'google-ads') return;
    try {
      const path = kind === 'instagram'  ? '/api/integrations/instagram/connect'
                 : kind === 'meta-ads'   ? '/api/integrations/meta-ads/connect'
                 :                         '/api/integrations/google-ads/connect';
      // Tell the backend where to bounce back after OAuth. The server
      // validates against FRONTEND_URLS allowlist and round-trips
      // through the OAuth state — see services/frontendOriginValidator.
      const redirect = typeof window !== 'undefined' ? window.location.origin : null;
      const res = await apiJson<{ authorizeUrl: string }>(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ redirect })
      });
      window.location.assign(res.authorizeUrl);
    } catch (err: unknown) {
      toast({
        title: 'Connect failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 4000
      });
    }
  };

  // Follow-up: disconnect still uses window.confirm. An in-app dialog
  // is a separate change so this path stays the one OAuth/disconnect
  // implementation.
  const disconnect = async (kind: 'instagram' | 'meta-ads' | 'google-ads', credentialId: string, name: string) => {
    const ok = window.confirm(`Disconnect ${name}? This stops syncs and removes the credential.`);
    if (!ok) return;
    try {
      await apiJson(`/api/integrations/${kind}/${credentialId}`, { method: 'DELETE' });
      toast({ title: `${name} disconnected`, status: 'success', duration: 2500 });
      await refresh();
    } catch (err: unknown) {
      toast({
        title: 'Disconnect failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 4000
      });
    }
  };

  // Shopify-specific handlers. Shop domain lives on the credential (not
  // in a global authorize URL), so Connect opens a modal to collect the
  // slug; the modal POSTs /shopify/connect itself and redirects the
  // window. Disconnect + Sync are plain fetches against the dedicated
  // Shopify endpoints.
  const shopifyDisconnect = async () => {
    const cred = shopifyStatus?.credential;
    if (!cred) return;
    const shopName = cred.shopInfo?.shopName || cred.shopDomain || 'Shopify';
    const ok = window.confirm(
      `Disconnect ${shopName}? This stops catalog sync + webhook updates. ` +
      `Lifestyle images we already pushed to Shopify stay there.`
    );
    if (!ok) return;
    try {
      await apiJson(`/api/integrations/shopify/${encodeURIComponent(brand._id)}`, { method: 'DELETE' });
      toast({ title: 'Shopify disconnected', status: 'success', duration: 2500 });
      await refresh();
    } catch (err: unknown) {
      toast({
        title: 'Disconnect failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 4000,
      });
    }
  };

  const shopifyToggleAutoPush = async (next: boolean) => {
    try {
      await apiJson('/api/integrations/shopify/sync-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-Brand-Id': brand._id },
        body: JSON.stringify({ shopifyAutoPushImages: next }),
      });
      // Optimistic local update + background re-fetch so the toggle
      // feels instant without round-trip latency.
      setShopifyStatus((prev) => prev ? ({
        ...prev,
        syncSettings: { shopifyAutoPushImages: next },
      }) : prev);
      toast({
        title: next ? 'Auto-push enabled' : 'Auto-push disabled',
        description: next
          ? 'Lifestyle images generated for Shopify-sourced products will push to Shopify automatically.'
          : 'Lifestyle images will stay in LiquidRetail only; nothing is pushed to Shopify until you re-enable.',
        status: next ? 'success' : 'info',
        duration: 3000,
      });
    } catch (err: unknown) {
      toast({
        title: 'Could not update setting',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 4000,
      });
      // Re-fetch to reconcile — tile shows the real server state.
      void refresh();
    }
  };

  const shopifySyncNow = async () => {
    setShopifySyncing(true);
    try {
      await apiJson(
        `/api/integrations/shopify/sync?brandId=${encodeURIComponent(brand._id)}`,
        { method: 'POST' }
      );
      toast({
        title: 'Shopify catalog sync started',
        description: 'Fetching products from Shopify in the background. Refresh to see updated counts.',
        status: 'success',
        duration: 3000,
      });
      // Refresh in a few seconds so the UI picks up lastCatalogSyncAt + productCount.
      setTimeout(() => { void refresh(); }, 3000);
    } catch (err: unknown) {
      toast({
        title: 'Shopify sync failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setShopifySyncing(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      // Fire catalog + posts in parallel; either may legitimately not
      // be configured (e.g. no IG attached). Settled allows the other
      // to land even when one fails.
      const results = await Promise.allSettled([
        apiJson('/api/integrations/instagram/sync-catalog', { method: 'POST' }),
        apiJson('/api/integrations/instagram/sync-posts',   { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
      ]);
      const errCount = results.filter(r => r.status === 'rejected').length;
      const okCount  = results.filter(r => r.status === 'fulfilled').length;
      toast({
        title: errCount === 0 ? 'Sync started' : `Sync partial (${okCount} ok, ${errCount} failed)`,
        description: errCount === 0
          ? 'Catalog + posts syncs are running.'
          : (results.find(r => r.status === 'rejected') as PromiseRejectedResult).reason?.message,
        status: errCount === 0 ? 'success' : 'warning',
        duration: 4000
      });
      await refresh();
    } finally {
      setSyncing(false);
    }
  };

  // The BILLABLE counterpart to syncNow above — force:true tells sync-posts
  // to re-enter posts it already ingested, refreshing their engagement
  // stats and queuing a detect run for each. Confirmed explicitly first
  // since every run costs money; scoped to this tile's own credential via
  // ?credentialId= so it can't accidentally force-rescan a different
  // account.
  //
  // CONTRACT GAP, verified against the shipped backend (services/
  // postSyncService.js + routes/integrations.js): this endpoint is
  // documented as bounded by "a daily detect cap" server-side, but that cap
  // is only ever threaded through from services/scheduledSyncService.js
  // (the automated cadence job) via a `dailyDetectRunCap` option. THIS
  // route never reads brand.syncSettings.dailyDetectRunCap and never passes
  // it into syncPosts(), so `runsRemaining` is always null for a manual
  // call and `capSkipped` can never actually be nonzero through this path —
  // the only real per-click bound is `limit` (defaults to 25, server-
  // clamped to 50). We still handle capSkipped below in case that gap gets
  // closed later, but the confirm copy deliberately does NOT claim a daily
  // ceiling — repeated manual clicks aren't throttled server-side today.
  const rescan = async (credentialId: string) => {
    const ok = window.confirm(
      'Re-scan Instagram? This re-processes up to 25 of the most recent posts for this account — ' +
      'refreshing engagement stats and queuing a billable detect run for each. Use Sync Now instead ' +
      'for the free, incremental sync.'
    );
    if (!ok) return;
    setRescanningId(credentialId);
    try {
      const res = await apiJson<SyncPostsResponse>(
        `/api/integrations/instagram/sync-posts?credentialId=${encodeURIComponent(credentialId)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ force: true }) }
      );
      // Defensive: this call is billable, so a response-shape surprise here
      // must not read as "the re-scan failed" — money may already be
      // spent. Coerce rather than let a missing/malformed field throw
      // inside the try and fall into the generic catch below.
      const reIngested  = Number(res?.reIngested) || 0;
      const ingested    = Number(res?.ingested) || 0;
      const errors      = Number(res?.errors) || 0;
      const capSkipped  = Number(res?.capSkipped) || 0;
      const queuedCount = Array.isArray(res?.queuedRunIds) ? res.queuedRunIds.length : 0;
      const hasErrors = errors > 0;
      // reIngested > 0 with nothing queued (or any capSkipped, see the
      // CONTRACT GAP note above) means the billable half of the job didn't
      // actually run — that's not the "complete" result a green toast
      // implies, even though every number in it is accurate.
      const detectBlocked = capSkipped > 0 || (reIngested > 0 && queuedCount === 0);
      toast({
        title: hasErrors
          ? `Re-scan finished with ${errors} error${errors === 1 ? '' : 's'}`
          : detectBlocked
          ? 'Re-scan ran, but no detect runs queued'
          : 'Re-scan complete',
        // Honest counters: reIngested is what a force re-scan actually
        // moves (already-known posts re-processed); ingested is genuinely
        // new posts, which a re-scan of an already-synced account rarely
        // turns up. "queued", not "re-queued" — a brand-new post's detect
        // run is a first-time queue, and queuedRunIds doesn't distinguish
        // the two, so "re-queued" would overclaim whenever ingested > 0.
        description: `${reIngested} post${reIngested === 1 ? '' : 's'} re-processed, ${ingested} new, `
          + `${queuedCount} detect run${queuedCount === 1 ? '' : 's'} queued`
          + (capSkipped > 0 ? `, ${capSkipped} skipped by the daily detect cap.` : '.'),
        status: hasErrors || detectBlocked ? 'warning' : 'success',
        duration: 6000
      });
      await refresh();
    } catch (err: unknown) {
      // sync-posts' own failure shape is { ok:false, reason } — Meta
      // errors, a missing igUserId, token decrypt failures, see
      // postSyncService.js — NOT { error } / { message }, so apiJson's
      // generic extraction misses it and err.message degrades to a bare
      // "HTTP 400". Read the real reason off the body first; this is the
      // billable path, so a useless error message is worse here than
      // almost anywhere else in this file.
      const body = isApiError(err) ? (err.body as { error?: string; reason?: string } | null) : null;
      toast({
        title: 'Re-scan failed',
        description: body?.error || body?.reason || (err instanceof Error ? err.message : 'Unknown error'),
        status: 'error',
        duration: 4000
      });
    } finally {
      setRescanningId(null);
    }
  };

  return (
    <Card variant="outline">
      <CardBody>
        <HStack justify="space-between" mb={4}>
          <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em" color="brand.ink">
            {only ? providerHeading(only) : 'Integrations'}
          </Heading>
          {!only && (
            <Button
              size="xs"
              variant="outline"
              isDisabled
              title="Connecting new platforms ships with each provider's row below"
            >
              Connect New Source
            </Button>
          )}
        </HStack>

        {loading ? (
          <Flex py={6} align="center" justify="center"><Spinner /></Flex>
        ) : (
          <SimpleGrid columns={only ? 1 : { base: 1, md: 2, lg: 5 }} spacing={3}>
            {(!only || only === 'instagram') && (
              <IntegrationTile
                kind="instagram"
                label="Instagram"
                gradient="linear-gradient(135deg, #FEDA77, #F58529, #DD2A7B, #8134AF, #515BD4)"
                status={igStatus}
                loadError={igError}
                onConnect={() => connect('instagram')}
                onDisconnect={(id) => disconnect('instagram', id, 'Instagram')}
                onManage={(id) => setIgPickerCredId(id)}
                onRescan={(id) => rescan(id)}
                rescanning={!!igStatus?.credential && rescanningId === igStatus.credential.id}
              />
            )}
            {(!only || only === 'meta-ads') && (
              <IntegrationTile
                kind="meta-ads"
                label="Meta Ads"
                gradient="#0866FF"
                status={metaStatus}
                loadError={metaError}
                onConnect={() => connect('meta-ads')}
                onDisconnect={(id) => disconnect('meta-ads', id, 'Meta Ads')}
                onManage={(id) => setAdsPickerCredId(id)}
              />
            )}
            {(!only || only === 'google-ads') && (
              <IntegrationTile
                kind="google-ads"
                label="Google Ads"
                gradient="linear-gradient(135deg, #4285F4, #34A853, #FBBC05, #EA4335)"
                status={googleStatus}
                loadError={googleError}
                onConnect={() => connect('google-ads')}
                onDisconnect={(id) => disconnect('google-ads', id, 'Google Ads')}
                onManage={(id) => setGoogleAdsPickerCredId(id)}
              />
            )}
            {(!only || only === 'shopify') && (
              <ShopifyTile
                status={shopifyStatus}
                loadError={shopifyError}
                onConnect={() => setShopifyModalOpen(true)}
                onDisconnect={() => void shopifyDisconnect()}
                onSync={() => void shopifySyncNow()}
                onToggleAutoPush={(v) => void shopifyToggleAutoPush(v)}
                syncing={shopifySyncing}
              />
            )}
            {!only && (
              <IntegrationTile
                kind="tiktok-shop"
                label="TikTok Shop"
                gradient="#000000"
                status={null}
                comingSoon
              />
            )}
          </SimpleGrid>
        )}

        {(!only || only === 'instagram') && (
        <>
        <Divider my={4} />

        <HStack justify="space-between" align="center">
          <HStack spacing={3}>
            <Box w="8px" h="8px" borderRadius="full" bg={brand.syncSettings?.autoSyncEnabled ? 'green.400' : 'gray.300'} />
            <Box>
              <Text fontSize="sm" fontWeight="700" color="brand.ink">
                Auto-sync is {brand.syncSettings?.autoSyncEnabled ? 'ON' : 'OFF'}
              </Text>
              <Text fontSize="xs" color="brand.muted">
                {brand.syncSettings?.autoSyncEnabled
                  ? `Catalog every ${brand.syncSettings?.catalogCadenceHours || 24}h · Posts every ${brand.syncSettings?.postsCadenceHours || 1}h · Campaigns every ${brand.syncSettings?.campaignCadenceHours || 6}h`
                  : 'Schedule disabled — toggle it in Automation Engine'}
              </Text>
            </Box>
          </HStack>
          <Button size="sm" variant="outline" onClick={syncNow} isLoading={syncing} leftIcon={<RefreshIcon />}>
            Sync Now
          </Button>
        </HStack>
        </>
        )}
      </CardBody>

      <IGPickerModal
        isOpen={!!igPickerCredId}
        onClose={() => setIgPickerCredId(null)}
        credentialId={igPickerCredId}
        onCompleted={refresh}
      />
      <AdsPickerModal
        isOpen={!!adsPickerCredId}
        onClose={() => setAdsPickerCredId(null)}
        credentialId={adsPickerCredId}
        onCompleted={refresh}
      />
      <GoogleAdsPickerModal
        isOpen={!!googleAdsPickerCredId}
        onClose={() => setGoogleAdsPickerCredId(null)}
        credentialId={googleAdsPickerCredId}
        onCompleted={refresh}
      />
      <ShopifyConnectModal
        isOpen={shopifyModalOpen}
        onClose={() => setShopifyModalOpen(false)}
        appMode={shopifyStatus?.appMode || 'custom'}
      />
    </Card>
  );
});

// Shopify tile — distinct from IntegrationTile below because its
// connected-state surface is different: shop name instead of
// @igHandle, planName + currency badges, product count, last webhook,
// and a Sync Now action right on the tile (vs the Instagram/Meta
// pattern where Sync Now sits in the bottom strip). The connected UX
// is deliberately richer here because Shopify is a bidirectional
// integration — we pull catalog AND push images, so the operator
// wants to see both axes healthy at a glance.
function ShopifyTile({
  status, loadError, onConnect, onDisconnect, onSync, onToggleAutoPush, syncing,
}: {
  status: ShopifyStatusRow | null;
  loadError?: string | null;
  onConnect:        () => void;
  onDisconnect:     () => void;
  onSync:           () => void;
  onToggleAutoPush: (next: boolean) => void;
  syncing:          boolean;
}) {
  if (loadError) {
    return (
      <Box
        data-testid="integration-tile-shopify"
        borderWidth="1px"
        borderColor="brand.border"
        borderRadius="lg"
        p={3}
        bg="brand.surface"
        display="flex"
        flexDirection="column"
        gap={2}
      >
        <HStack justify="space-between" align="center">
          <Text fontWeight="700" color="brand.ink">Shopify</Text>
          <Badge colorScheme="red" fontSize="2xs">Error</Badge>
        </HStack>
        <Text fontSize="xs" color="red.700" data-testid="integration-error-shopify">{loadError}</Text>
      </Box>
    );
  }

  const cred = status?.credential || null;
  const isConnected = !!cred;
  const shopName   = cred?.shopInfo?.shopName || cred?.shopDomain || null;
  const planName   = cred?.shopInfo?.planName || null;
  const currency   = cred?.shopInfo?.currencyCode || null;
  const products   = cred?.productCount != null ? cred.productCount : null;
  const lastSync   = cred?.lastCatalogSyncAt ? timeSince(cred.lastCatalogSyncAt) : null;
  const lastHook   = cred?.lastWebhookAt     ? timeSince(cred.lastWebhookAt)     : null;
  const autoPush   = !!status?.syncSettings?.shopifyAutoPushImages;

  return (
    <Box
      data-testid="integration-tile-shopify"
      borderWidth="1px"
      borderColor="brand.border"
      borderRadius="lg"
      p={3}
      bg="brand.surface"
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <HStack justify="space-between" align="center">
        <HStack spacing={2}>
          <Box
            w="24px"
            h="24px"
            borderRadius="full"
            bg="linear-gradient(135deg, #96bf48, #5e8e3e)"
            flexShrink={0}
          />
          <Text fontWeight="700" color="brand.ink">Shopify</Text>
        </HStack>
        {isConnected ? (
          <Badge colorScheme="green" fontSize="2xs">Connected</Badge>
        ) : (
          <Badge colorScheme="gray" fontSize="2xs">Not connected</Badge>
        )}
      </HStack>

      {isConnected ? (
        <VStack align="stretch" spacing={1}>
          <Text fontSize="sm" fontWeight="600" color="brand.ink" noOfLines={1}>
            {shopName}
          </Text>
          <HStack spacing={1.5} fontSize="2xs" color="brand.muted" wrap="wrap">
            {planName && <Badge colorScheme="purple" variant="subtle">{planName}</Badge>}
            {currency && <Badge variant="subtle">{currency}</Badge>}
            {cred?.shopInfo?.isDevStore && <Badge colorScheme="orange" variant="subtle">Dev store</Badge>}
          </HStack>
          <HStack justify="space-between" fontSize="xs" color="brand.muted">
            <Text>Products</Text>
            <Text fontWeight="600" color="brand.ink">
              {products != null ? products.toLocaleString() : '—'}
            </Text>
          </HStack>
          {lastSync && (
            <HStack justify="space-between" fontSize="xs" color="brand.muted">
              <Text>Last sync</Text>
              <Text>{lastSync}</Text>
            </HStack>
          )}
          {lastHook && (
            <HStack justify="space-between" fontSize="xs" color="brand.muted">
              <Text>Last event</Text>
              <Text>{lastHook}</Text>
            </HStack>
          )}
          <HStack
            mt={1.5}
            spacing={2}
            align="center"
            px={2}
            py={1.5}
            borderRadius="md"
            bg={autoPush ? 'rgba(149,191,72,0.08)' : 'transparent'}
            borderWidth="1px"
            borderColor={autoPush ? 'rgba(149,191,72,0.3)' : 'brand.border'}
            title="When ON, every lifestyle image generated for a Shopify-sourced product is pushed to Shopify as an additional product image automatically."
          >
            <Checkbox
              size="sm"
              isChecked={autoPush}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onToggleAutoPush(e.target.checked)}
            >
              <Text fontSize="xs" color="brand.ink" fontWeight={autoPush ? '700' : '500'}>
                Auto-push lifestyle images
              </Text>
            </Checkbox>
          </HStack>
          <HStack mt={2} spacing={2}>
            <Button size="xs" variant="outline" onClick={onSync} isLoading={syncing} flex={1}>
              Sync
            </Button>
            <Button size="xs" variant="ghost" colorScheme="red" onClick={onDisconnect} flex={1}>
              Disconnect
            </Button>
          </HStack>
        </VStack>
      ) : status?.appMode !== 'custom' && status?.appMode ? (
        // Public distribution: no operator in the loop, no allowlist to
        // request against. Direct Install only. The access-request UI
        // below only renders in 'custom' mode.
        <>
          <Text fontSize="xs" color="brand.muted" flex={1}>
            Connect your Shopify store to sync your catalog and push
            lifestyle images onto your product listings.
          </Text>
          <Button size="sm" colorScheme="purple" onClick={onConnect} mt={2} w="100%">
            Install Shopify
          </Button>
        </>
      ) : status?.accessRequest?.status === 'ready' ? (
        <>
          <Box bg="green.50" borderWidth="1px" borderColor="green.300" borderRadius="md" p={2} mt={1}>
            <Text fontSize="xs" color="green.800" fontWeight="600">
              ✓ Approved — ready to install
            </Text>
            <Text fontSize="2xs" color="green.700" mt={0.5}>
              Store <Text as="code">{status.accessRequest.shopDomain}</Text> is on the
              approved list. Click Install to grant us access to your Shopify catalog.
            </Text>
          </Box>
          <Button size="sm" colorScheme="purple" onClick={onConnect} mt={2} w="100%">
            Install
          </Button>
        </>
      ) : status?.accessRequest?.status === 'pending' ? (
        <>
          <Box bg="blue.50" borderWidth="1px" borderColor="blue.300" borderRadius="md" p={2} mt={1}>
            <Text fontSize="xs" color="blue.800" fontWeight="600">
              ⏳ Awaiting approval
            </Text>
            <Text fontSize="2xs" color="blue.700" mt={0.5}>
              Store <Text as="code">{status.accessRequest.shopDomain}</Text> submitted.
              The Reach Social team is adding it to Shopify (usually within an hour).
              This card will update automatically — safe to close.
            </Text>
          </Box>
          <Button size="xs" variant="ghost" onClick={onConnect} mt={2}>
            Open status window
          </Button>
        </>
      ) : status?.accessRequest?.status === 'rejected' ? (
        <>
          <Box bg="orange.50" borderWidth="1px" borderColor="orange.300" borderRadius="md" p={2} mt={1}>
            <Text fontSize="xs" color="orange.800" fontWeight="600">
              Previous request rejected
            </Text>
            {status.accessRequest.notes && (
              <Text fontSize="2xs" color="orange.700" mt={0.5}>
                Reason: {status.accessRequest.notes}
              </Text>
            )}
          </Box>
          <Button size="xs" colorScheme="purple" onClick={onConnect} mt={2}>
            Try a different store
          </Button>
        </>
      ) : (
        <>
          <Text fontSize="xs" color="brand.muted" flex={1}>
            Connect your Shopify store to sync your catalog and push
            lifestyle images onto your product listings.
          </Text>
          <Button size="xs" colorScheme="purple" onClick={onConnect} mt={2}>
            Connect Shopify
          </Button>
        </>
      )}
    </Box>
  );
}

function providerHeading(only: DirectConnection): string {
  if (only === 'meta-ads') return 'Meta Ads';
  if (only === 'google-ads') return 'Google Ads';
  if (only === 'instagram') return 'Instagram';
  return 'Shopify';
}

function IntegrationTile({
  kind, label, gradient, status, loadError, onConnect, onDisconnect, onManage, onRescan, rescanning, comingSoon
}: {
  kind: IntegrationKind;
  label: string;
  gradient: string;
  status: StatusRow | null;
  loadError?: string | null;
  onConnect?:    () => void;
  onDisconnect?: (credentialId: string) => void;
  onManage?:     (credentialId: string) => void;
  // Only passed for the Instagram tile — same "optional prop gates optional
  // UI" pattern as onManage/onDisconnect above, so Re-scan only renders
  // where it's wired up instead of needing a `kind === 'instagram'` check.
  onRescan?:     (credentialId: string) => void;
  rescanning?:   boolean;
  comingSoon?:   boolean;
}) {
  const cred = status?.credential || null;
  const isConnected = !!cred && cred.status === 'active';
  const isPending   = !!cred && cred.status === 'pending';
  const accountLabel = cred?.igUsername      ? `@${cred.igUsername}`
                    : cred?.pageName        ? cred.pageName
                    : cred?.selectedAdAccountName ? cred.selectedAdAccountName
                    : cred?.accountLabel    ? cred.accountLabel
                    : null;
  const lastSynced = cred?.lastUsedAt ? timeSince(cred.lastUsedAt) : null;

  return (
    <Box
      data-testid={`integration-tile-${kind}`}
      borderWidth="1px"
      borderColor="brand.border"
      borderRadius="lg"
      p={3}
      bg="brand.surface"
      h="100%"
    >
      <HStack spacing={3} align="flex-start" mb={2}>
        <Box
          w="36px" h="36px"
          flexShrink={0}
          borderRadius="md"
          bg={typeof gradient === 'string' && gradient.startsWith('linear') ? undefined : gradient}
          bgImage={typeof gradient === 'string' && gradient.startsWith('linear') ? gradient : undefined}
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="white"
          fontWeight="800"
          fontSize="lg"
        >
          {label.charAt(0)}
        </Box>
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={1}>{label}</Text>
          <StatusBadge connected={isConnected} pending={isPending} comingSoon={comingSoon} failed={!!loadError} />
        </Box>
      </HStack>

      <VStack align="stretch" spacing={1} mb={3} minH="40px">
        {accountLabel && (
          <Text fontSize="xs" color="brand.ink" noOfLines={1}>{accountLabel}</Text>
        )}
        {lastSynced && (
          <Text fontSize="xs" color="brand.muted">Last synced {lastSynced}</Text>
        )}
        {isPending && (
          <Text fontSize="10px" color="orange.600">Setup incomplete — finish on the legacy Brand page.</Text>
        )}
        {comingSoon && (
          <Text fontSize="10px" color="brand.muted" fontStyle="italic">Provider integration not yet wired.</Text>
        )}
      </VStack>

      {loadError ? (
        <Text fontSize="xs" color="red.700" data-testid={`integration-error-${kind}`}>{loadError}</Text>
      ) : comingSoon ? (
        <Button size="xs" variant="outline" w="100%" isDisabled>Connect</Button>
      ) : isConnected || isPending ? (
        <VStack align="stretch" spacing={2}>
          <HStack spacing={2}>
            <Button
              size="xs"
              variant={isPending ? 'brand' : 'outline'}
              flex={1}
              onClick={() => cred && onManage?.(cred.id)}
            >
              {isPending ? 'Finish setup' : 'Manage'}
            </Button>
            <Button
              size="xs"
              variant="outline"
              colorScheme="red"
              flex={1}
              onClick={() => cred && onDisconnect?.(cred.id)}
            >
              Disconnect
            </Button>
          </HStack>
          {/* Hidden while pending — nothing has synced yet to re-scan. */}
          {onRescan && isConnected && (
            <Button
              size="xs"
              variant="outline"
              w="100%"
              onClick={() => cred && onRescan(cred.id)}
              isLoading={rescanning}
              loadingText="Re-scanning…"
            >
              Re-scan
            </Button>
          )}
        </VStack>
      ) : (
        <Button size="xs" variant="brand" w="100%" onClick={onConnect}>Connect</Button>
      )}
    </Box>
  );
}

function StatusBadge({ connected, pending, comingSoon, failed }: { connected: boolean; pending: boolean; comingSoon?: boolean; failed?: boolean }) {
  if (comingSoon) return <Badge fontSize="9px" colorScheme="gray" variant="subtle">Coming soon</Badge>;
  if (failed)     return <Badge fontSize="9px" colorScheme="red" variant="subtle">Error</Badge>;
  if (pending)    return <Badge fontSize="9px" colorScheme="orange" variant="subtle">Pending setup</Badge>;
  if (connected)  return <Badge fontSize="9px" colorScheme="green" variant="subtle">Connected</Badge>;
  return <Badge fontSize="9px" colorScheme="gray" variant="subtle">Not connected</Badge>;
}

function timeSince(iso: string): string {
  try {
    const t = new Date(iso).getTime();
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60)  return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60)  return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24)  return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return iso; }
}

function RefreshIcon() {
  return <Icon viewBox="0 0 24 24" w="14px" h="14px"><path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.7 10h-2.06A6 6 0 1 1 12 6a5.95 5.95 0 0 1 4.22 1.78L13 11h7V4l-2.35 2.35z" /></Icon>;
}
